import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { forumApi, discussionApi } from "../lib";
import { showError, showSuccess } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft,
  MessageCircle,
  Users,
  Pin,
  Search,
  Plus,
  MessageSquare,
  Eye,
  CheckCircle,
  X,
  Send,
  ChevronDown,
  Bell,
  BellOff,
  Loader,
  Flame,
  Grid3x3,
  List,
  Clock,
  Home,
  Award,
  Calendar,
  TrendingUp,
  ChevronRight,
  Filter,
  Hash,
  Bookmark,
  Share2,
  Reply,
  Heart,
  BarChart3,
  Zap,
  Settings,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Save,
  Sparkles,
  GraduationCap,
  ThumbsUp,
  AlertCircle,
  Menu,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Author {
  user: string;
  name: string;
  avatar?: string;
  role: string;
  badge?: string;
  posts: number;
}
interface Reply {
  _id: string;
  id: string;
  author: Author;
  content: string;
  createdAt: string;
  likes: number;
  likedBy: string[];
  isSolution: boolean;
  isApproved: boolean;
}
interface Discussion {
  _id: string;
  id: string;
  title: string;
  content: string;
  author: Author;
  category: string;
  tags: string[];
  views: number;
  likes: number;
  likedBy: string[];
  isPinned: boolean;
  isLocked: boolean;
  isHot: boolean;
  isResolved: boolean;
  replyCount: number;
  lastActivity: string;
  createdAt: string;
  replies?: Reply[];
}
interface ForumInfo {
  _id: string;
  id: string;
  title: string;
  description: string;
  course: string;
  courseTitle: string;
  courseThumbnail?: string;
  category: string;
  memberCount: number;
  topics: number;
  posts: number;
  instructor: Author;
  notificationSettings: { isNotifying: boolean };
  isJoined?: boolean;
  isActive?: boolean;
  requireModeration?: boolean;
}

const ForumDetailPage = () => {
  const { forumId } = useParams<{ forumId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [forum, setForum] = useState<ForumInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState("latest");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedDiscussion, setExpandedDiscussion] = useState<string | null>(
    null,
  );
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    category: "",
    tags: "",
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    category: "",
    isActive: true,
    requireModeration: false,
  });
  const [isMobile, setIsMobile] = useState(false);

  const sortRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    fetchForumData();
  }, [forumId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node))
        setShowSortMenu(false);
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target as Node) &&
        mobileMenuOpen
      ) {
        setMobileMenuOpen(false);
      }
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target as Node) &&
        showCreateModal
      ) {
        setShowCreateModal(false);
      }
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target as Node) &&
        showEditModal
      ) {
        setShowEditModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCreateModal, showEditModal, mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const fetchForumData = async () => {
    if (!forumId) return;
    try {
      setLoading(true);
      const [forumRes, discsRes] = await Promise.all([
        forumApi.getById(forumId),
        discussionApi.getByForum(forumId, { limit: 50 }),
      ]);
      if (forumRes.success) setForum(forumRes.data);
      if (discsRes.success) setDiscussions(discsRes.data);
    } catch {
      showError("Failed to load forum");
    } finally {
      setLoading(false);
    }
  };

  const filtered = discussions
    .filter(
      (d) =>
        (!searchQuery ||
          d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.content.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (activeCategory === "All" || d.category === activeCategory),
    )
    .sort((a, b) => {
      if (sortBy === "popular") return (b.likes || 0) - (a.likes || 0);
      if (sortBy === "unanswered")
        return (a.replyCount || 0) - (b.replyCount || 0);
      if (sortBy === "pinned") return Number(b.isPinned) - Number(a.isPinned);
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const toggleLike = async (id: string) => {
    try {
      const res = await discussionApi.toggleLike(id);
      if (res.success)
        setDiscussions((p) =>
          p.map((d) => (d._id === id ? { ...d, likes: res.data.likes } : d)),
        );
    } catch {}
  };

  const handleCreate = async () => {
    if (!newPost.title || !newPost.content || !newPost.category || !forumId) {
      showError("Please fill all required fields");
      return;
    }
    try {
      setSubmitting(true);
      const res = await discussionApi.create(forumId, {
        title: newPost.title,
        content: newPost.content,
        category: newPost.category,
        tags: newPost.tags ? newPost.tags.split(",").map((t) => t.trim()) : [],
      });
      if (res.success) {
        showSuccess("Discussion created!");
        setShowCreateModal(false);
        setNewPost({ title: "", content: "", category: "", tags: "" });
        fetchForumData();
      }
    } catch (e: any) {
      showError(e.message || "Failed to create discussion");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (id: string) => {
    if (!replyText[id]?.trim()) return;
    try {
      const res = await discussionApi.addReply(id, replyText[id]);
      if (res.success) {
        showSuccess("Reply added!");
        setReplyText((p) => ({ ...p, [id]: "" }));
        fetchForumData();
      }
    } catch {
      showError("Failed to add reply");
    }
  };

  const toggleNotif = async () => {
    if (!forumId || !forum) return;
    try {
      const next = !forum.notificationSettings?.isNotifying;
      await forumApi.toggleNotifications(forumId, next);
      setForum((p) =>
        p
          ? {
              ...p,
              notificationSettings: {
                ...p.notificationSettings,
                isNotifying: next,
              },
            }
          : null,
      );
    } catch {}
  };

  const toggleJoin = async () => {
    if (!forumId || !forum) return;
    try {
      if (forum.isJoined) {
        await forumApi.leaveForum(forumId);
        setForum((p) =>
          p
            ? { ...p, isJoined: false, memberCount: (p.memberCount || 1) - 1 }
            : null,
        );
        showSuccess("Left the forum");
      } else {
        await forumApi.joinForum(forumId);
        setForum((p) =>
          p
            ? { ...p, isJoined: true, memberCount: (p.memberCount || 0) + 1 }
            : null,
        );
        showSuccess("Joined forum!");
      }
    } catch (e: any) {
      showError(e.message || "Failed");
    }
  };

  const canManage =
    user &&
    forum &&
    (user.role === "admin" || user.id === forum.instructor?.user);

  const handleUpdateForum = async () => {
    if (!forumId) return;
    try {
      setSubmitting(true);
      const res = await forumApi.update(forumId, editForm);
      if (res.success) {
        setForum((p) => (p ? { ...p, ...editForm } : null));
        showSuccess("Forum updated");
        setShowEditModal(false);
      }
    } catch (e: any) {
      showError(e.message || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteForum = async () => {
    if (
      !forumId ||
      !confirm("Delete this forum? All discussions will be lost.")
    )
      return;
    try {
      await forumApi.delete(forumId);
      showSuccess("Forum deleted");
      navigate("/forums");
    } catch (e: any) {
      showError(e.message || "Failed to delete");
    }
  };

  const openEditModal = () => {
    if (forum) {
      setEditForm({
        title: forum.title,
        description: forum.description || "",
        category: forum.category,
        isActive: forum.isActive ?? true,
        requireModeration: forum.requireModeration ?? false,
      });
      setShowEditModal(true);
    }
  };

  const fmtTime = (s: string) => {
    const d = new Date(s),
      now = new Date(),
      ms = now.getTime() - d.getTime();
    const m = Math.floor(ms / 60000),
      h = Math.floor(ms / 3600000),
      day = Math.floor(ms / 86400000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m`;
    if (h < 24) return `${h}h`;
    if (day < 7) return `${day}d`;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  const cats = [
    "All",
    ...new Set(discussions.map((d) => d.category).filter(Boolean)),
  ];
  const allTags = discussions.flatMap((d) => d.tags || []);
  const tagFreq = allTags.reduce<Record<string, number>>((a, t) => {
    a[t] = (a[t] || 0) + 1;
    return a;
  }, {});
  const popularTags = Object.entries(tagFreq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);
  const authorFreq = discussions.reduce<
    Record<string, { name: string; count: number; avatar?: string }>
  >((a, d) => {
    const k = d.author?.user || d.author?.name;
    if (!k) return a;
    if (!a[k])
      a[k] = { name: d.author.name, count: 0, avatar: d.author.avatar };
    a[k].count++;
    return a;
  }, {});
  const topContribs = Object.values(authorFreq)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-10 h-10 mx-auto mb-4 text-orange-500 animate-spin" />
          <p className="text-sm font-mono text-gray-400">Loading forum...</p>
        </div>
      </div>
    );
  }

  if (!forum) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="max-w-md p-6 mx-4 text-center bg-white shadow-xl rounded-2xl">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full">
            <MessageCircle className="w-8 h-8 text-orange-500" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Forum not found
          </h2>
          <p className="mb-6 text-gray-500">
            This forum doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/forums")}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Forums
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      {/* ==================== FIXED HEADER - AT THE VERY TOP ==================== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            {/* Left side - Back button & Title */}
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => navigate("/forums")}
                className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-3.5 h-3.5 text-white" />
                </div>
                <h1 className="text-sm font-semibold text-gray-900 truncate sm:text-base">
                  {forum.title}
                </h1>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Join/Leave button - Desktop */}
              <button
                onClick={toggleJoin}
                className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  forum.isJoined
                    ? "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                    : "bg-gradient-to-r from-orange-600 to-orange-500 text-white hover:shadow-md"
                }`}
              >
                {forum.isJoined ? (
                  <>
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Joined</span>
                  </>
                ) : (
                  <>
                    <Users className="w-3.5 h-3.5" />
                    <span>Join</span>
                  </>
                )}
              </button>

              {/* Notification toggle */}
              <button
                onClick={toggleNotif}
                className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 transition-colors"
                title={
                  forum.notificationSettings?.isNotifying ? "Mute" : "Notify"
                }
              >
                {forum.notificationSettings?.isNotifying ? (
                  <Bell className="w-4 h-4 text-orange-500" />
                ) : (
                  <BellOff className="w-4 h-4" />
                )}
              </button>

              {/* New Discussion button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg hover:shadow-md transition-all"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">New</span>
              </button>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Stats badge - Desktop */}
              <div className="hidden lg:flex items-center gap-3 text-xs text-gray-500 ml-2 pl-3 border-l border-gray-200">
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-3.5 h-3.5" />
                  {forum.topics || 0}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {forum.memberCount || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer to account for fixed header */}
      <div className="h-14 lg:h-16" />

      {/* Main Content */}
      <div className="px-4 py-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:py-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          {/* Main Content - Discussions */}
          <div className="flex-1 min-w-0">
            {/* Search & Filter Bar */}
            <div className="flex flex-col gap-3 mb-5">
              <div className="relative">
                <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                <input
                  type="text"
                  placeholder="Search discussions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 overflow-x-auto pb-1">
                  {cats.slice(0, isMobile ? 3 : 4).map((c) => (
                    <button
                      key={c}
                      onClick={() => setActiveCategory(c)}
                      className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap ${
                        activeCategory === c
                          ? "bg-orange-500 text-white shadow-sm"
                          : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <div className="relative" ref={sortRef}>
                    <button
                      onClick={() => setShowSortMenu(!showSortMenu)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
                    >
                      <Filter className="w-3 h-3" />
                      <span className="capitalize hidden sm:inline">
                        {sortBy}
                      </span>
                      <ChevronDown
                        className={`w-3 h-3 transition-transform ${showSortMenu ? "rotate-180" : ""}`}
                      />
                    </button>
                    {showSortMenu && (
                      <div className="absolute right-0 z-20 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[120px]">
                        {["latest", "popular", "unanswered", "pinned"].map(
                          (s) => (
                            <button
                              key={s}
                              onClick={() => {
                                setSortBy(s);
                                setShowSortMenu(false);
                              }}
                              className={`w-full px-3 py-2 text-left text-xs hover:bg-orange-50 transition-colors ${
                                sortBy === s
                                  ? "text-orange-600 font-semibold"
                                  : "text-gray-700"
                              }`}
                            >
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </button>
                          ),
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg whitespace-nowrap"
                  >
                    <Plus className="w-3 h-3" /> New
                  </button>
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex items-center justify-end gap-2">
                <div className="flex overflow-hidden border border-gray-200 rounded-lg">
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 transition-colors ${viewMode === "list" ? "bg-orange-500 text-white" : "text-gray-500"}`}
                  >
                    <List className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-1.5 transition-colors ${viewMode === "grid" ? "bg-orange-500 text-white" : "text-gray-500"}`}
                  >
                    <Grid3x3 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Category Chips - Full List */}
              <div className="flex gap-2 pb-1 overflow-x-auto">
                {cats.map((c) => (
                  <button
                    key={c}
                    onClick={() => setActiveCategory(c)}
                    className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap ${
                      activeCategory === c
                        ? "bg-orange-500 text-white shadow-sm"
                        : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Discussions List - No text truncation */}
            {filtered.length === 0 ? (
              <div className="py-12 text-center bg-white border border-gray-100 rounded-xl">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full">
                  <MessageCircle className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="mb-2 text-base font-semibold text-gray-900">
                  No discussions yet
                </h3>
                <p className="mb-5 text-sm text-gray-500">
                  Be the first to start the conversation.
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl font-semibold"
                >
                  <Plus className="w-4 h-4" /> Start a Discussion
                </button>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filtered.map((d) => (
                  <div
                    key={d._id}
                    onClick={() => navigate(`/forums/${forumId}/${d._id}`)}
                    className="p-4 transition-all bg-white border border-gray-100 cursor-pointer rounded-xl hover:shadow-lg"
                  >
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {d.isPinned && (
                        <Pin className="w-3 h-3 text-orange-500" />
                      )}
                      {d.isHot && <Flame className="w-3 h-3 text-orange-500" />}
                      {d.isResolved && (
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                      )}
                      <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                        {d.category}
                      </span>
                    </div>
                    <h3 className="mb-2 text-sm font-bold text-gray-900">
                      {d.title}
                    </h3>
                    <p className="mb-3 text-sm text-gray-600">{d.content}</p>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-[10px] font-bold">
                          {d.author?.name?.charAt(0) || "U"}
                        </div>
                        <span className="text-xs text-gray-600 truncate max-w-[80px]">
                          {d.author?.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {d.replyCount || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          {d.likes || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((d) => {
                  const isExp = expandedDiscussion === d._id;
                  const isLiked = (d.likedBy || []).includes(user?.id || "");
                  return (
                    <div
                      key={d._id}
                      className="overflow-hidden bg-white border border-gray-100 rounded-xl"
                    >
                      <div className="p-4">
                        <div className="flex gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xs font-bold">
                              {d.author?.name?.charAt(0) || "U"}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                              {d.isPinned && (
                                <Pin className="w-3 h-3 text-orange-500" />
                              )}
                              {d.isHot && (
                                <Flame className="w-3 h-3 text-orange-500" />
                              )}
                              {d.isResolved && (
                                <CheckCircle className="w-3 h-3 text-emerald-500" />
                              )}
                              <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                                {d.category}
                              </span>
                            </div>
                            <h3
                              onClick={() =>
                                navigate(`/forums/${forumId}/${d._id}`)
                              }
                              className="text-sm font-bold text-gray-900 mb-1.5 cursor-pointer hover:text-orange-600"
                            >
                              {d.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">
                              {d.content}
                            </p>
                            {d.tags && d.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-2">
                                {d.tags.slice(0, 3).map((tag, i) => (
                                  <span
                                    key={i}
                                    className="text-[9px] px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded-full"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {fmtTime(d.createdAt)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {d.views || 0}
                              </span>
                              <span className="text-gray-600">
                                by {d.author?.name}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Bar */}
                      <div className="flex items-center gap-1 px-4 py-2 border-t border-gray-100 bg-gray-50">
                        <button
                          onClick={() => toggleLike(d._id)}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-all ${
                            isLiked
                              ? "text-rose-500 bg-rose-50"
                              : "text-gray-500 hover:bg-gray-100"
                          }`}
                        >
                          <Heart
                            className={`w-4 h-4 ${isLiked ? "fill-rose-500" : ""}`}
                          />
                          <span>{d.likes || 0}</span>
                        </button>
                        <button
                          onClick={() =>
                            setExpandedDiscussion(isExp ? null : d._id)
                          }
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-all ${
                            isExp
                              ? "text-orange-600 bg-orange-50"
                              : "text-gray-500 hover:bg-gray-100"
                          }`}
                        >
                          <Reply className="w-4 h-4" />
                          <span>{d.replyCount || 0}</span>
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/forums/${forumId}/${d._id}`)
                          }
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 transition-all ml-auto"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span>View Thread</span>
                        </button>
                      </div>

                      {/* Replies Section */}
                      {isExp && (
                        <div className="px-4 pt-0 pb-4 bg-gray-50">
                          <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
                            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                              {(d.replies || []).length > 0 ? (
                                (d.replies || []).map((rep, idx) => (
                                  <div key={rep.id || idx} className="p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-bold">
                                        {rep.author?.name?.charAt(0) || "U"}
                                      </div>
                                      <span className="text-xs font-medium text-gray-900">
                                        {rep.author?.name}
                                      </span>
                                      {rep.isSolution && (
                                        <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                                          Solution
                                        </span>
                                      )}
                                      <span className="ml-auto text-[10px] text-gray-400">
                                        {fmtTime(rep.createdAt)}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 ml-7">
                                      {rep.content}
                                    </p>
                                  </div>
                                ))
                              ) : (
                                <div className="p-4 text-center text-xs text-gray-400">
                                  No replies yet
                                </div>
                              )}
                            </div>
                            <div className="p-3 border-t border-gray-100 bg-gray-50">
                              <div className="flex gap-2">
                                <textarea
                                  rows={2}
                                  placeholder="Write a reply..."
                                  value={replyText[d._id] || ""}
                                  onChange={(e) =>
                                    setReplyText((p) => ({
                                      ...p,
                                      [d._id]: e.target.value,
                                    }))
                                  }
                                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                                />
                                <button
                                  onClick={() => handleReply(d._id)}
                                  disabled={!replyText[d._id]?.trim()}
                                  className="px-4 py-2 text-white bg-orange-500 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block w-80 xl:w-96 flex-shrink-0">
            <div className="sticky space-y-5 top-20">
              {/* Forum Hero Card */}
              <div className="p-6 text-white shadow-xl bg-gradient-to-br from-orange-600 to-orange-500 rounded-2xl">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                    {forum.category}
                  </span>
                  {forum.notificationSettings?.isNotifying && (
                    <span className="px-2 py-0.5 bg-emerald-500/30 rounded-full text-xs font-semibold flex items-center gap-1">
                      <Bell className="w-3 h-3" /> Live
                    </span>
                  )}
                </div>
                <h2 className="text-xl font-bold mb-2">{forum.title}</h2>
                <p className="text-sm text-orange-100 mb-4">
                  {forum.description ||
                    `Discussion space for ${forum.courseTitle}`}
                </p>
                <div className="flex items-center gap-3 mb-5 text-sm text-orange-100">
                  <span className="flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" />
                    {forum.instructor?.name || "Instructor"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {forum.courseTitle}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={toggleJoin}
                    className={`flex-1 px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                      forum.isJoined
                        ? "bg-white/20 text-white hover:bg-white/30"
                        : "bg-white text-orange-600 hover:shadow-lg"
                    }`}
                  >
                    {forum.isJoined ? "Joined ✓" : "Join Forum"}
                  </button>
                  <button
                    onClick={toggleNotif}
                    className="px-4 py-2 bg-white/20 rounded-xl text-sm font-semibold hover:bg-white/30"
                  >
                    {forum.notificationSettings?.isNotifying ? (
                      <BellOff className="w-4 h-4" />
                    ) : (
                      <Bell className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    label: "Discussions",
                    value: forum.topics || 0,
                    icon: MessageSquare,
                  },
                  {
                    label: "Replies",
                    value: forum.posts || 0,
                    icon: MessageCircle,
                  },
                  {
                    label: "Members",
                    value: forum.memberCount || 0,
                    icon: Users,
                  },
                  {
                    label: "Resolved",
                    value: discussions.filter((d) => d.isResolved).length,
                    icon: CheckCircle,
                  },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="p-4 text-center bg-white border border-gray-100 shadow-sm rounded-xl"
                  >
                    <stat.icon className="w-5 h-5 mx-auto mb-2 text-orange-500" />
                    <div className="text-2xl font-bold text-gray-900">
                      {stat.value}
                    </div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Popular Tags */}
              {popularTags.length > 0 && (
                <div className="p-5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <h3 className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-900">
                    <Hash className="w-4 h-4 text-orange-500" /> Popular Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {popularTags.map(([tag, count]) => (
                      <button
                        key={tag}
                        onClick={() => setSearchQuery(tag)}
                        className="text-xs px-2.5 py-1 bg-orange-50 text-orange-600 rounded-full hover:bg-orange-100"
                      >
                        #{tag}{" "}
                        <span className="ml-1 text-orange-400">{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Contributors */}
              {topContribs.length > 0 && (
                <div className="p-5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <h3 className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-900">
                    <Users className="w-4 h-4 text-orange-500" /> Top
                    Contributors
                  </h3>
                  <div className="space-y-3">
                    {topContribs.map((c) => (
                      <div key={c.name} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xs font-bold">
                          {c.name?.charAt(0) || "U"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {c.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {c.count} posts
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Card */}
              <div className="p-5 text-center border border-orange-100 bg-gradient-to-br from-orange-50 to-orange-100/30 rounded-xl">
                <Zap className="w-8 h-8 mx-auto mb-3 text-orange-500" />
                <h3 className="mb-1 text-sm font-bold text-gray-900">
                  Got a question?
                </h3>
                <p className="mb-4 text-xs text-gray-600">
                  Start a discussion and get help from the community.
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full py-2.5 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-xl font-semibold text-sm hover:shadow-lg"
                >
                  <Plus className="inline w-4 h-4 mr-1" /> Start a Discussion
                </button>
              </div>

              {/* Admin Controls */}
              {canManage && (
                <div className="p-5 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <h3 className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-900">
                    <Settings className="w-4 h-4 text-gray-500" /> Admin
                    Controls
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={openEditModal}
                      className="flex items-center w-full gap-2 px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-orange-50 hover:text-orange-600"
                    >
                      <Edit className="w-4 h-4" /> Edit Forum
                    </button>
                    <button
                      onClick={handleDeleteForum}
                      className="flex items-center w-full gap-2 px-3 py-2 text-sm text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" /> Delete Forum
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Discussion Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) =>
            e.target === e.currentTarget && setShowCreateModal(false)
          }
        >
          <div
            className="w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
            ref={modalRef}
          >
            <div className="px-5 py-4 bg-gradient-to-r from-orange-600 to-orange-500">
              <h2 className="text-lg font-bold text-white">
                Start a Discussion
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <input
                type="text"
                placeholder="Title *"
                value={newPost.title}
                onChange={(e) =>
                  setNewPost((p) => ({ ...p, title: e.target.value }))
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
              <select
                value={newPost.category}
                onChange={(e) =>
                  setNewPost((p) => ({ ...p, category: e.target.value }))
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              >
                <option value="">Select category *</option>
                {[
                  "Technical Help",
                  "General Discussion",
                  "Project Showcase",
                  "Resources",
                  "Announcements",
                ].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <textarea
                rows={4}
                placeholder="Content *"
                value={newPost.content}
                onChange={(e) =>
                  setNewPost((p) => ({ ...p, content: e.target.value }))
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
              <input
                type="text"
                placeholder="Tags (comma-separated, optional)"
                value={newPost.tags}
                onChange={(e) =>
                  setNewPost((p) => ({ ...p, tags: e.target.value }))
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={
                  submitting ||
                  !newPost.title ||
                  !newPost.content ||
                  !newPost.category
                }
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl hover:shadow-lg disabled:opacity-50"
              >
                {submitting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  "Post"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Forum Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) =>
            e.target === e.currentTarget && setShowEditModal(false)
          }
        >
          <div className="w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-orange-600 to-orange-500">
              <h2 className="text-lg font-bold text-white">Edit Forum</h2>
            </div>
            <div className="p-5 space-y-4">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, title: e.target.value }))
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
              <textarea
                rows={3}
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, description: e.target.value }))
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl resize-none"
              />
              <input
                type="text"
                value={editForm.category}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, category: e.target.value }))
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
              />
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateForum}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl hover:shadow-lg"
              >
                {submitting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ForumDetailPage;
